import React, { useState, useEffect } from "react";
import GeneralModal from "usefulComponents/Modal/Modal";
import ThemedSelect from "components/fields/ThemedSelect";

export interface SearchOption {
  value: string;
  label: string;
}

interface SearchFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (
    searchType: "description" | "system",
    selectedValue: string,
    selectedLabel: string
  ) => void;
  onClear: () => void;
  descriptionOptions: SearchOption[];
  systemOptions: SearchOption[];
  currentSearchType?: "description" | "system" | null;
  currentSearchValue?: string | null;
}

const SearchFilterModal: React.FC<SearchFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  descriptionOptions,
  systemOptions,
  currentSearchType,
  currentSearchValue,
}) => {
  const [searchType, setSearchType] = useState<"description" | "system">("description");
  const [selectedOption, setSelectedOption] = useState<SearchOption | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (currentSearchType) {
        setSearchType(currentSearchType);
        if (currentSearchType === "description") {
          const match = descriptionOptions.find((o) => o.value === currentSearchValue);
          setSelectedOption(match || null);
        } else {
          const match = systemOptions.find((o) => o.value === currentSearchValue);
          setSelectedOption(match || null);
        }
      } else {
        setSearchType("description");
        setSelectedOption(null);
      }
    }
  }, [isOpen, currentSearchType, currentSearchValue, descriptionOptions, systemOptions]);

  const handleTypeChange = (type: "description" | "system") => {
    setSearchType(type);
    setSelectedOption(null);
  };

  const handleSave = () => {
    if (!selectedOption) {
      alert("Please select an option first!");
      return;
    }
    onApply(searchType, selectedOption.value, selectedOption.label);
    onClose();
  };

  return (
    <GeneralModal
      openModal={isOpen}
      title="Search & Filter"
      saveButtonText="Apply Search"
      cancelButtonText="Cancel"
      onClose={onClose}
      onSave={handleSave}
    >
      <div className="mt-6 w-full text-left">
        <p className="mb-4 text-sm font-medium text-textSecondary">
          Select a search type and choose an option below:
        </p>

        {/* Toggle buttons for search type */}
        <div className="mb-5 flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleTypeChange("description")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold border transition-all duration-200 cursor-pointer ${
              searchType === "description"
                ? "bg-brand-500 text-white border-brand-500 shadow-sm"
                : "bg-surfaceSecondary text-textSecondary border-borderPrimary hover:border-brand-500 hover:text-brand-500"
            }`}
          >
            Description
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange("system")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold border transition-all duration-200 cursor-pointer ${
              searchType === "system"
                ? "bg-brand-500 text-white border-brand-500 shadow-sm"
                : "bg-surfaceSecondary text-textSecondary border-borderPrimary hover:border-brand-500 hover:text-brand-500"
            }`}
          >
            Systems
          </button>
        </div>

        {/* Dropdown for selected type */}
        <div className="w-full">
          <label className="ml-1.5 text-sm font-medium text-textPrimary">
            {searchType === "description" ? "Select Description" : "Select System"}
          </label>
          <div className="mt-2">
            <ThemedSelect
              name="searchSelect"
              placeholder={
                searchType === "description"
                  ? "Choose a description..."
                  : "Choose a system..."
              }
              value={selectedOption}
              options={searchType === "description" ? descriptionOptions : systemOptions}
              onChange={(option: any) => setSelectedOption(option)}
              isMulti={false}
              isClearable={true}
            />
          </div>
        </div>

        {/* Clear filter button if search is active */}
        {currentSearchType && currentSearchValue && (
          <div className="mt-5 flex justify-end border-t border-borderPrimary pt-4">
            <button
              type="button"
              onClick={() => {
                onClear();
                onClose();
              }}
              className="text-xs font-semibold text-red-500 hover:text-red-600 underline cursor-pointer transition-colors duration-150"
            >
              Clear Current Filter
            </button>
          </div>
        )}
      </div>
    </GeneralModal>
  );
};

export default SearchFilterModal;
